import { create } from 'zustand';
import { audioContext } from './context';

export interface AudioDevice {
  id: string;
  label: string;
}

/** The id standing for "whatever the system is set to", which is where we start. */
export const SYSTEM_DEFAULT = '';

/**
 * Picking an output means re-pointing the whole context, which only Chromium
 * has. Everywhere else the output follows the system and the picker says so.
 */
const sinkContext = audioContext as AudioContext & { setSinkId?: (id: string) => Promise<void> };
export const OUTPUT_SELECTABLE = typeof sinkContext.setSinkId === 'function';

interface DeviceStore {
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  inputId: string;
  outputId: string;
  /**
   * Device labels come back blank until the user has granted microphone access
   * once, so until then the list can only be numbered.
   */
  named: boolean;
}

export const useDeviceStore = create<DeviceStore>(() => ({
  inputs: [],
  outputs: [],
  inputId: SYSTEM_DEFAULT,
  outputId: SYSTEM_DEFAULT,
  named: false,
}));

/** The name to show for a device, falling back to its place in the list. */
export function deviceLabel(device: AudioDevice, index: number, kind: string): string {
  return device.label || `${kind} ${index + 1}`;
}

export function selectedLabel(devices: AudioDevice[], id: string, kind: string): string {
  if (id === SYSTEM_DEFAULT) return 'System default';
  const index = devices.findIndex((device) => device.id === id);
  return index < 0 ? 'Unavailable' : deviceLabel(devices[index], index, kind);
}

let stream: MediaStream | null = null;
let opening: Promise<MediaStream> | null = null;

function constraint(): MediaTrackConstraints | true {
  const { inputId } = useDeviceStore.getState();
  return inputId === SYSTEM_DEFAULT ? true : { deviceId: { exact: inputId } };
}

/**
 * Opens a capture stream on the selected input, reusing the live one if there
 * is one. The permission prompt lands here, which is to say when the user
 * actually starts a take rather than on the way in.
 */
export function openInput(): Promise<MediaStream> {
  if (stream?.active === true) return Promise.resolve(stream);

  const capture = (audio: MediaTrackConstraints | true) =>
    navigator.mediaDevices.getUserMedia({ audio, video: false });

  opening ??= capture(constraint())
    .catch((error: unknown) => {
      // A device chosen earlier can be unplugged by the time a take starts, and
      // an exact `deviceId` then fails outright rather than falling back. Better
      // to record through whatever is still there and say so than to record
      // nothing at all.
      if (useDeviceStore.getState().inputId === SYSTEM_DEFAULT) throw error;
      useDeviceStore.setState({ inputId: SYSTEM_DEFAULT });
      return capture(true);
    })
    .then((opened) => {
      stream = opened;
      // A grant is what fills in the labels, so the list is worth another look.
      void refreshDevices();
      return opened;
    })
    .finally(() => {
      opening = null;
    });

  return opening;
}

/** Drops the capture stream, which is what turns the recording indicator off. */
export function releaseInput(): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
  stream = null;
}

export async function refreshDevices(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;

  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
  const of = (kind: MediaDeviceKind): AudioDevice[] =>
    devices
      .filter((device) => device.kind === kind && device.deviceId !== SYSTEM_DEFAULT)
      .map((device) => ({ id: device.deviceId, label: device.label }));

  const inputs = of('audioinput');
  const outputs = of('audiooutput');

  useDeviceStore.setState({
    inputs,
    outputs,
    named: [...inputs, ...outputs].some((device) => device.label !== ''),
  });
}

/** Points sampling at a different input. The next take opens it. */
export function selectInput(id: string): void {
  if (useDeviceStore.getState().inputId === id) return;
  releaseInput();
  useDeviceStore.setState({ inputId: id });
}

/** Re-points the whole audio context, so everything already playing moves too. */
export async function selectOutput(id: string): Promise<void> {
  if (!OUTPUT_SELECTABLE || useDeviceStore.getState().outputId === id) return;
  try {
    await sinkContext.setSinkId!(id);
    useDeviceStore.setState({ outputId: id });
  } catch {
    // A device can vanish between listing it and choosing it; the context stays
    // on whatever it was already playing through.
    void refreshDevices();
  }
}

export function installDeviceWatch(): () => void {
  const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  if (!media) return () => {};

  const onChange = () => void refreshDevices();
  media.addEventListener('devicechange', onChange);
  void refreshDevices();

  return () => {
    media.removeEventListener('devicechange', onChange);
    releaseInput();
  };
}
